import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { addSeconds, differenceInSeconds } from 'date-fns';
import { useLocalStorage } from 'usehooks-ts';

import { MirrorService } from 'services/MirrorService';
import { PixelConfig, PixelType } from 'types/interfaces/ads/RetargetingPixels';

import { ORGANIC_PARTNER_IDS } from 'helpers/constants';
import { isMonetizationPage } from 'helpers/route';
import { useLocationChange } from 'hooks/useLocationChange';
import {
  getAuthenticated,
  getIsBuyer,
  getIsUserRegisterOnLand,
  getUserPartnerId,
  getUserPaymentSubmit,
} from 'store/auth/selectors';
import { getRetargetDomains } from 'store/common/selectors';

import {
  EXO_DOMAIN_PLACEHOLDER,
  PIXEL_CONFIGS,
  RETARGETING_CONFIG,
  STORAGE_KEYS,
} from '../constants';
import { useBotDetector } from './useBotDetector';

const generatePixelUrls = (
  domains: string[],
  config: PixelConfig
): string[] => {
  const exoUrls =
    domains?.map((domain) =>
      config.exoClickUrl.replace(EXO_DOMAIN_PLACEHOLDER, domain)
    ) || [];

  return [config.trafficJunkyUrl, ...exoUrls];
};

const createCooldownTime = (): string => {
  const newCooldownTime = addSeconds(
    new Date(),
    RETARGETING_CONFIG.cooldownSeconds
  );

  return String(newCooldownTime);
};

export const useRetargetingPixels = () => {
  const { pathname } = useLocation();
  const [isNotFirstPage, setIsNotFirstPage] = useState(false);

  const isAuthenticated = useSelector(getAuthenticated);
  const isBuyer = useSelector(getIsBuyer);
  const isRegisterFromLand = useSelector(getIsUserRegisterOnLand);
  const isPaymentSubmit = useSelector(getUserPaymentSubmit);
  const userPartnerId = useSelector(getUserPartnerId);
  const exoDomains = useSelector(getRetargetDomains);

  const [pixelCooldown, setPixelCooldown] = useLocalStorage<string | null>(
    STORAGE_KEYS.RETARGETING_PIXEL_COOLDOWN,
    null
  );

  const { isUserBot } = useBotDetector();

  const isCooldownExpired = useCallback(() => {
    if (!pixelCooldown) return true;

    return differenceInSeconds(new Date(), new Date(pixelCooldown)) > 0;
  }, [pixelCooldown]);

  const handleAllPixelLoaded = useCallback(() => {
    setPixelCooldown(createCooldownTime());
  }, [setPixelCooldown]);

  const pixelUrls = useMemo(() => {
    const priorityConfigs = [
      { condition: isBuyer, priority: PixelType.Init },
      { condition: isPaymentSubmit, priority: PixelType.Submit },
      {
        condition:
          isRegisterFromLand &&
          !!userPartnerId &&
          userPartnerId > RETARGETING_CONFIG.minPartnerId,
        priority: PixelType.Reg,
      },
    ];

    return priorityConfigs
      .filter(({ condition }) => condition)
      ?.map(({ priority }) => {
        const config = PIXEL_CONFIGS[priority];

        return generatePixelUrls(exoDomains, config);
      })
      ?.flat();
  }, [isBuyer, isPaymentSubmit, isRegisterFromLand, userPartnerId, exoDomains]);

  const shouldLoadPixels = useMemo(() => {
    const isSiteGroupValid =
      MirrorService.isFirstSiteGroup || MirrorService.isSecondSiteGroup;
    const isUserValid = isAuthenticated && !isUserBot;
    const isPartnerValid =
      !!userPartnerId && !ORGANIC_PARTNER_IDS.includes(String(userPartnerId));
    const isCooldownValid = isCooldownExpired();
    const isPageValid = !isMonetizationPage(pathname) && isNotFirstPage;

    return (
      isSiteGroupValid &&
      isUserValid &&
      isPageValid &&
      isPartnerValid &&
      isCooldownValid
    );
  }, [
    isAuthenticated,
    isCooldownExpired,
    isNotFirstPage,
    isUserBot,
    pathname,
    userPartnerId,
  ]);

  useLocationChange(({ prevUrl }) => {
    if (!!prevUrl && !isNotFirstPage) setIsNotFirstPage(true);
  });

  return {
    pixelUrls: shouldLoadPixels ? pixelUrls : null,
    handleAllPixelLoaded,
  };
};
